import { forwardRef, type CSSProperties } from "react";
import FbkCardShell from "./FbkCardShell";
import { ThemeOverlay } from "./ThemeOverlay";
import { resolveCardTheme } from "./theme";
import {
  defaultFooterLeft,
  resolveTextStyle,
  type CardFormat,
  type StoryPage,
  type UserStoryCardContent,
} from "./types";

interface UserStoryCardProps {
  format: CardFormat;
  content: UserStoryCardContent;
  /** Which page index to render (defaults to 0 — the cover) */
  pageIndex?: number;
}

const UserStoryCard = forwardRef<HTMLDivElement, UserStoryCardProps>(
  function UserStoryCard({ format, content, pageIndex = 0 }, ref) {
    const isStory = format === "story";
    const style = content.style ?? {};
    const themeId = content.theme ?? "signature";
    const theme = resolveCardTheme(themeId, { monoColor: content.monoColor });
    const isEditorial = themeId === "editorial";

    const pages = content.pages.length > 0 ? content.pages : [BLANK_PAGE];
    const page: StoryPage = pages[Math.min(pageIndex, pages.length - 1)];
    const totalPages = pages.length;
    const isCover = pageIndex === 0;

    // ─── Styles ───────────────────────────────────────────────────
    const eyebrowStyle: CSSProperties = {
      ...resolveTextStyle(
        style.eyebrow,
        {
          fontFamily: theme.fontEyebrow,
          fontSizePx: 22,
          color: theme.accentColor,
          letterSpacing: "0.4em",
          bold: true,
        },
        format,
      ),
      textTransform: "uppercase",
    };

    const headlineDefaultSize = isStory
      ? (isCover ? 200 : 168)
      : (isCover ? 148 : 124);
    const headlineStyle: CSSProperties = {
      ...resolveTextStyle(
        style.headline,
        {
          fontFamily: theme.fontHeadline,
          fontSizePx: headlineDefaultSize,
          color: theme.headlineColor,
          letterSpacing: isEditorial ? "-0.015em" : "0.005em",
          bold: isEditorial ? true : false,
          italic: isEditorial,
        },
        format,
      ),
      lineHeight: isEditorial ? 1.0 : 0.88,
      marginTop: isStory ? 20 : 14,
    };

    const goldStyle: CSSProperties = {
      ...resolveTextStyle(
        style.goldHighlight,
        {
          fontFamily: theme.fontHeadline,
          fontSizePx: headlineDefaultSize,
          color: theme.accentColor,
          bold: isEditorial ? true : false,
          italic: isEditorial,
        },
        format,
      ),
      display: "block",
      lineHeight: isEditorial ? 1.0 : 0.88,
      fontSize:
        (style.headline?.fontSizePx ??
          style.goldHighlight?.fontSizePx ??
          headlineDefaultSize) * (isStory ? 1 : 0.78),
    };

    const taglineStyle: CSSProperties = {
      ...resolveTextStyle(
        style.tagline,
        {
          fontFamily: theme.fontBody,
          fontSizePx: 36,
          color: theme.accentColor,
          bold: false,
          italic: true,
        },
        format,
      ),
      lineHeight: 1.3,
      marginTop: isStory ? 32 : 22,
      paddingLeft: isStory ? 28 : 22,
      borderLeft: `4px solid ${theme.quoteBarColor}`,
    };

    const bodyResolved = resolveTextStyle(
      style.body,
      {
        fontFamily: theme.fontBody,
        fontSizePx: 26,
        color: theme.subtleTextColor,
        bold: false,
      },
      format,
    );
    const bodyStyle: CSSProperties = {
      ...bodyResolved,
      lineHeight: 1.55,
      marginTop: isStory ? 32 : 22,
      whiteSpace: "pre-wrap",
    };

    const statsLineStyle: CSSProperties = {
      fontFamily: theme.fontBody,
      fontSize: isStory ? 18 : 16,
      fontWeight: 700,
      letterSpacing: "0.25em",
      textTransform: "uppercase",
      color: `${theme.accentColor}cc`, // ~80%
      marginTop: isStory ? 32 : 22,
      paddingTop: isStory ? 18 : 14,
      borderTop: `1px solid ${theme.accentColor}40`, // ~25%
    };

    const ctaFontSize = isStory ? 24 : 20;
    const ctaStyle: CSSProperties = {
      ...resolveTextStyle(
        style.ctaText,
        {
          fontFamily: theme.fontBody,
          fontSizePx: 24,
          color: theme.ctaFg,
          bold: true,
        },
        format,
      ),
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
      marginTop: isStory ? 36 : 22,
      padding: isStory ? "18px 32px" : "14px 26px",
      borderRadius: 9999,
      background: theme.ctaBg,
      fontSize: ctaFontSize,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      boxShadow: `0 20px 50px ${theme.ctaBg}40`,
      alignSelf: "flex-start",
      textAlign: undefined,
    };

    // Drop cap — theme accent color
    const dropCapSize = (bodyResolved.fontSize as number) * 3.4;
    const dropCapStyle: CSSProperties = {
      float: "left",
      fontFamily: theme.fontHeadline,
      fontSize: dropCapSize,
      lineHeight: 0.85,
      color: theme.accentColor,
      marginRight: 12,
      marginTop: 4,
      letterSpacing: "-0.02em",
    };

    // Portrait sizing
    const portraitWidth = isStory ? 380 : 360;
    const portraitHeight = isStory ? 1280 : 820;

    const chapterChip = totalPages > 1 && (
      <div
        style={{
          display: "inline-flex",
          alignItems: "baseline",
          gap: 8,
          fontFamily: theme.fontBody,
          fontSize: isStory ? 16 : 14,
          fontWeight: 800,
          color: `${theme.accentColor}b3`, // ~70%
          letterSpacing: "0.4em",
          textTransform: "uppercase",
          marginBottom: 10,
        }}
      >
        <span style={{ fontFamily: theme.fontHeadline, fontSize: isStory ? 40 : 32, color: theme.accentColor, lineHeight: 1 }}>
          {String(pageIndex + 1).padStart(2, "0")}
        </span>
        <span style={{ color: `${theme.accentColor}66` }}>
          / {String(totalPages).padStart(2, "0")}
        </span>
      </div>
    );

    // Per-chapter background overrides the card-level Background tab when set.
    const pageBackground = page.backgroundImageUrl
      ? { imageUrl: page.backgroundImageUrl, opacity: style.background?.opacity ?? 0.3 }
      : style.background;

    return (
      <FbkCardShell
        ref={ref}
        format={format}
        footerLeft={content.footerLeft || defaultFooterLeft("USER_STORY")}
        footerRight={content.hashtags || "#WC2026 · #FBK · #STORY"}
        header={content.header}
        background={pageBackground}
        customBaseGradient={theme.customBaseGradient}
        headerOnLight={theme.headerOnLight}
        suppressBrandOverlays={theme.suppressBrandOverlays}
        hideFooter={content.footer?.hide}
      >
        <ThemeOverlay theme={theme} format={format} />
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "row",
            gap: isStory ? 48 : 36,
            alignItems: "stretch",
            paddingTop: isStory ? 48 : 32,
            paddingBottom: isStory ? 24 : 18,
            minHeight: 0,
            position: "relative",
            zIndex: 2,
          }}
        >
          {/* LEFT — TEXT */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minWidth: 0,
            }}
          >
            {chapterChip}
            <div style={eyebrowStyle}>{page.eyebrow}</div>

            <div style={headlineStyle}>
              {page.name}
              {page.goldHighlight && (
                <span style={goldStyle}>{page.goldHighlight}</span>
              )}
            </div>

            {page.tagline && <div style={taglineStyle}>"{page.tagline}"</div>}
            {page.body && (
              <div style={bodyStyle}>
                <DroppedBody body={page.body} dropCapStyle={dropCapStyle} />
              </div>
            )}
            {page.statsLine && (
              <div style={statsLineStyle}>{page.statsLine}</div>
            )}
            {page.ctaText && <div style={ctaStyle}>{page.ctaText} →</div>}
          </div>

          {/* RIGHT — PORTRAIT (floating: filter:drop-shadow follows the alpha
              channel, so cutout PNGs cast a true silhouette shadow; rectangular
              photos still get a soft floating shadow on their bounding box.) */}
          {page.portraitImageUrl && (
            <div
              style={{
                width: portraitWidth,
                flexShrink: 0,
                alignSelf: "center",
                position: "relative",
              }}
            >
              <img
                src={page.portraitImageUrl}
                alt=""
                crossOrigin={
                  page.portraitImageUrl.startsWith("data:") ? undefined : "anonymous"
                }
                style={{
                  display: "block",
                  width: "100%",
                  height: portraitHeight,
                  maxHeight: isStory ? "calc(100vh - 200px)" : portraitHeight,
                  objectFit: "contain",
                  filter:
                    "drop-shadow(0 30px 60px rgba(0,0,0,0.55)) drop-shadow(0 12px 24px rgba(0,0,0,0.4))",
                }}
              />
            </div>
          )}
        </div>
      </FbkCardShell>
    );
  },
);

function DroppedBody({
  body,
  dropCapStyle,
}: {
  body: string;
  dropCapStyle: CSSProperties;
}) {
  const trimmed = body.replace(/^\s+/, "");
  if (!trimmed) return null;
  const firstChar = trimmed.charAt(0);
  const rest = trimmed.slice(1);
  const paragraphs = rest.split(/\n\n+/);
  const firstPara = paragraphs[0] ?? "";
  const tail = paragraphs.slice(1);
  return (
    <>
      <p style={{ margin: 0 }}>
        <span style={dropCapStyle}>{firstChar}</span>
        {firstPara}
      </p>
      {tail.map((p, i) => (
        <p key={i} style={{ margin: "0.9em 0 0 0" }}>
          {p}
        </p>
      ))}
    </>
  );
}

const BLANK_PAGE: StoryPage = {
  eyebrow: "",
  name: "",
  body: "",
};

export default UserStoryCard;
